import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { arrayUnion, collection, deleteDoc, doc, getFirestore, limit, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { ISSUE_TYPES } from '../components/issues/IssueReporter';
import { toMs } from '../office/today/collections';
import { LAYOUT_OWNER_EMAIL } from '../office/today/useTodayPrefs';
import '../office/today/today.css';
import '../components/issues/issuesPage.css';

const STATUSES = [
  { value: 'new', label: 'New', tone: 'acc' },
  { value: 'in_progress', label: 'In progress', tone: 'warn' },
  { value: 'fixed', label: 'Fixed', tone: 'good' },
  { value: 'wont_fix', label: "Won't fix", tone: 'mute' },
];
const statusOf = (v: string) => STATUSES.find((s) => s.value === v) || STATUSES[0];
const typeLabel = (v: string) => ISSUE_TYPES.find((t) => t.value === v)?.label || 'Other';
const ago = (ms: number) => {
  if (!ms) return '';
  const m = Math.round((Date.now() - ms) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return d < 30 ? `${d} day${d === 1 ? '' : 's'} ago` : new Date(ms).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const when = (ms: number) => (ms ? new Date(ms).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

/** Office › Reported issues: every "Report a problem" sent from the app. Only the owner account can see this page. */
export default function OfficeIssuesPage() {
  const { currentUser, actorUser } = useAuth();
  const me = actorUser || currentUser;
  const isOwner = String(me?.email || '').toLowerCase() === LAYOUT_OWNER_EMAIL;
  const navigate = useNavigate();
  const db = useMemo(() => getFirestore(), []);
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<string>('open');
  const [type, setType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState('');
  const [note, setNote] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!isOwner) return;
    return onSnapshot(
      query(collection(db, 'issueReports'), orderBy('createdAt', 'desc'), limit(500)),
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setLoading(false);
      },
      (err) => {
        setError(err?.code === 'permission-denied' ? 'Reports are not readable yet: the new Firestore rule needs to be deployed.' : "Couldn't load reports.");
        setLoading(false);
      }
    );
  }, [db, isOwner]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { open: 0, all: rows.length };
    rows.forEach((r) => {
      c[r.status || 'new'] = (c[r.status || 'new'] || 0) + 1;
      if (r.status !== 'fixed' && r.status !== 'wont_fix') c.open += 1;
    });
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const st = r.status || 'new';
      if (status === 'open' ? st === 'fixed' || st === 'wont_fix' : status !== 'all' && st !== status) return false;
      if (type !== 'all' && r.type !== type) return false;
      if (!q) return true;
      return [r.comment, r.page?.path, r.reporterName, r.reporterEmail, r.projectName, r.unitId, r.element?.text].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, search, status, type]);

  const cur = rows.find((r) => r.id === selected) || shown[0] || null;
  useEffect(() => {
    setConfirmDelete(false);
    setNote('');
  }, [cur?.id]);

  const setIssueStatus = (id: string, value: string) =>
    updateDoc(doc(db, 'issueReports', id), { status: value, updatedAt: serverTimestamp(), updatedBy: me?.email || null }).catch((err) => setError(err?.message || "Couldn't change the status."));
  const addNote = async () => {
    if (!cur || !note.trim()) return;
    try {
      await updateDoc(doc(db, 'issueReports', cur.id), { notes: arrayUnion({ text: note.trim(), at: Date.now(), by: me?.email || '' }), updatedAt: serverTimestamp() });
      setNote('');
    } catch (err: any) {
      setError(err?.message || "Couldn't add the note.");
    }
  };
  const remove = async () => {
    if (!cur) return;
    try {
      await deleteDoc(doc(db, 'issueReports', cur.id));
      setSelected('');
    } catch (err: any) {
      setError(err?.message || "Couldn't delete the report.");
    }
  };

  if (!isOwner) {
    return (
      <div className="ot ois">
        <div className="ot-card ot-blank"><h3 style={{ margin: '0 0 6px' }}>Reported issues</h3><p className="muted" style={{ margin: 0 }}>Only the account owner can see reported issues.</p></div>
      </div>
    );
  }

  return (
    <div className="ot ois">
      <header className="ois-head">
        <div>
          <h1>Reported issues</h1>
          <p>{counts.open} open · {rows.length} total · sent from the flag button on every page</p>
        </div>
        <label className="ois-search">
          <span className="sr">Search reports</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search comment, page, person, unit" />
        </label>
      </header>

      <div className="ois-filters">
        <div className="ois-seg" role="group" aria-label="Status">
          {[{ value: 'open', label: 'Open' }, ...STATUSES, { value: 'all', label: 'All' }].map((s) => (
            <button key={s.value} aria-pressed={status === s.value} onClick={() => setStatus(s.value)}>{s.label}<b>{counts[s.value] || 0}</b></button>
          ))}
        </div>
        <div className="ois-seg" role="group" aria-label="Kind">
          <button aria-pressed={type === 'all'} onClick={() => setType('all')}>All kinds</button>
          {ISSUE_TYPES.map((t) => <button key={t.value} aria-pressed={type === t.value} onClick={() => setType(t.value)}>{t.label}</button>)}
        </div>
      </div>
      {error ? <div className="ot-alert" role="alert">{error}</div> : null}

      <div className="ois-grid">
        <section className="ois-list" aria-label="Reports">
          {loading ? <p className="faint">Loading reports…</p> : null}
          {!loading && !shown.length ? <p className="faint ois-empty">{rows.length ? 'No reports match these filters.' : 'No reports yet. They appear here when someone uses the flag button.'}</p> : null}
          {shown.map((r) => {
            const st = statusOf(r.status || 'new');
            return (
              <button key={r.id} className={`ois-item${cur?.id === r.id ? ' on' : ''}`} onClick={() => setSelected(r.id)}>
                <span className="top">
                  <span className={`ot-chip c-${st.tone}`}>{st.label}</span>
                  <span className="faint">{typeLabel(r.type)}</span>
                  <span className="faint when">{ago(toMs(r.createdAt))}</span>
                </span>
                <span className="t">{r.comment}</span>
                <span className="s">{r.page?.path || '/'}{r.unitId ? ` · ${r.unitId}` : ''} · {r.reporterName || r.reporterEmail || 'Someone'}</span>
              </button>
            );
          })}
        </section>

        <section className="ois-detail" aria-label="Report details">
          {!cur ? (
            <p className="faint">Pick a report to see it.</p>
          ) : (
            <>
              <div className="ois-d-hd">
                <div>
                  <span className="ot-lbl">{typeLabel(cur.type)} · {when(toMs(cur.createdAt))}</span>
                  <h2>{cur.comment}</h2>
                </div>
                <div className="ois-d-actions">
                  <button className="ot-btn sm dark" onClick={() => navigate(`${cur.page?.path || '/'}${cur.page?.search || ''}`)}>Open this page</button>
                </div>
              </div>

              <div className="ois-status" role="group" aria-label="Status">
                {STATUSES.map((s) => (
                  <button key={s.value} aria-pressed={(cur.status || 'new') === s.value} onClick={() => setIssueStatus(cur.id, s.value)} className={`t-${s.tone}`}>{s.label}</button>
                ))}
              </div>

              {cur.element ? (
                <div className="ois-box">
                  <span className="ot-lbl">Picked on the page</span>
                  <p className="ois-quote">“{cur.element.text || cur.element.label || cur.element.tag}”</p>
                  <code>{cur.element.path}</code>
                </div>
              ) : null}

              <dl className="ois-facts">
                <div><dt>Page</dt><dd><code>{cur.page?.path}{cur.page?.search || ''}</code> <span className="faint">({cur.page?.app === 'office' ? 'Office' : 'CRM'})</span></dd></div>
                {cur.projectName || cur.projectId ? <div><dt>Project</dt><dd>{cur.projectName || cur.projectId}</dd></div> : null}
                {cur.unitId ? <div><dt>Unit</dt><dd>{cur.unitId}</dd></div> : null}
                <div><dt>Reported by</dt><dd>{cur.reporterName || '—'} <span className="faint">{cur.reporterEmail}{cur.reporterRole ? ` · ${cur.reporterRole}` : ''}</span></dd></div>
                {cur.viewingAs ? <div><dt>Viewing as</dt><dd>{cur.viewingAs.name} <span className="faint">{cur.viewingAs.roleId}</span></dd></div> : null}
                <div><dt>Device</dt><dd>{cur.device?.viewport} window{cur.device?.touch ? ' · touch' : ''} <span className="faint">{cur.device?.userAgent}</span></dd></div>
                <div><dt>App version</dt><dd><code>{cur.appVersion}</code></dd></div>
              </dl>

              <div className="ois-notes">
                <span className="ot-lbl">Notes</span>
                {(cur.notes || []).length ? (
                  <ol>
                    {(cur.notes || []).map((n: any, i: number) => <li key={i}><p>{n.text}</p><span className="faint">{n.by} · {when(Number(n.at))}</span></li>)}
                  </ol>
                ) : <p className="faint">No notes yet.</p>}
                <div className="ois-note-add">
                  <label className="sr" htmlFor="ois-note">Add a note</label>
                  <textarea id="ois-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note, for example what was fixed" />
                  <button className="ot-btn sm" onClick={addNote} disabled={!note.trim()}>Add note</button>
                </div>
              </div>

              <div className="ois-del">
                {confirmDelete ? (
                  <span>Delete this report for good? <button className="ois-link danger" onClick={remove}>Delete</button> <button className="ois-link" onClick={() => setConfirmDelete(false)}>Keep it</button></span>
                ) : (
                  <button className="ois-link danger" onClick={() => setConfirmDelete(true)}>Delete report</button>
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
